import React, {useEffect, useState} from 'react';

import cs from 'classnames';
import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {ViewModeTitle} from 'app/general/detailpage/ViewModeTitle';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';

import {SingleQuestionContent} from './SingleQuestionContent';

import type {
  AnswerChoiceType,
  AnswerFreeformType,
  AttemptAnswerType,
  AttemptSerializedType,
  QuestionType,
  QuizVersionSerializedType,
} from 'sweet-types';

type Props = {
  content?: QuizVersionSerializedType['content'];
  attempt: AttemptSerializedType;
  onSubmitAttempt?: () => Promise<void>;
  onRetry?: () => Promise<void>;
  onAnswer?: (props: {
    answer: AttemptAnswerType;
    question: QuestionType;
  }) => unknown;
  isAnsweringQuestion?: boolean;
  isloadingAnswer?: boolean;
  duration?: number;
};

type SelectionProps = {
  selections: Array<AnswerChoiceType | AnswerFreeformType>;
  correctAnswers: Record<string, boolean>;
  currentQuestion: number;
  gotoQuestion: (index: number) => void;
};

type SelectionButtonProps = SelectionProps & {
  n: number;
  question: QuestionType;
};

export const AttemptQuizContent = ({
  content,
  attempt,
  onAnswer,
  onSubmitAttempt,
  onRetry,
  isAnsweringQuestion,
  duration,
}: Props) => {
  const [freeformText, setFreeformText] = useState<Record<string, string>>({});
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<Array<string>>([]);
  const user = useCurrentUserState().data!;

  const question = attempt.questions[currentQuestion];

  useEffect(() => {
    const answerIndex = attempt.answers.findIndex(
      (item) => item.question_id === question.id
    );
    const answer = attempt.answers[answerIndex];

    if (answer?.type === 'checkbox') {
      setSelectedAnswer(answer.answer_id);
    } else {
      setSelectedAnswer([]);
    }
  }, [attempt.answers, currentQuestion]);

  let correctAnswers: Record<string, boolean> = {};
  content?.forEach((question) => {
    question.answers.forEach((answer) => {
      correctAnswers[answer.id] = answer.correct;
    });
  });
  const questionCount = attempt.questions.length;
  const answerCount = attempt.answers.filter((answer) =>
    answer.type === 'checkbox' ? answer.answer_id.length > 0 : true
  ).length;

  const progress = Math.floor((answerCount / questionCount) * 100);
  const isIncompleteAttempt = attempt.status === 'pending' && progress != 100;

  const gotoQuestion = (page) => {
    setCurrentQuestion(Math.min(page, questionCount - 1));
  };

  const nextQuestion = () => {
    setCurrentQuestion((index) => Math.min(index + 1, questionCount - 1));
  };

  const prevQuestion = () => {
    setCurrentQuestion((index) => Math.min(index - 1, questionCount - 1));
  };

  useEffect(() => {
    attempt?.answers
      .filter((answer) => answer.type === 'freeform')
      .map((answer: AnswerFreeformType) => {
        setFreeformText((prev) => ({
          ...prev,
          [answer.question_id]: answer.freeform,
        }));
      });
  }, [attempt?.id]);

  const handleAnswer = ({
    selectedAnswerId,
    question,
  }: {
    selectedAnswerId: string;
    question: QuestionType;
    freeform: string;
  }) => {
    if (selectedAnswer.length === 0 && onAnswer) {
      selectedAnswerId && setSelectedAnswer([selectedAnswerId]);
      onAnswer({
        answer: {answer_id: [selectedAnswerId]},
        question,
      });
    }
  };

  const handleFreeform = ({
    freeform,
    question,
  }: {
    freeform: string;
    question: QuestionType;
  }) => {
    onAnswer &&
      onAnswer({
        answer: {freeform},
        question,
      });
  };

  return (
    <>
      <ViewModeTitle className="mb-2 px-0" title={gt('Questions')} />
      {question ? (
        <>
          <SingleQuestionContent
            index={currentQuestion}
            duration={duration}
            question={question}
            attempt={attempt}
            freeform={freeformText[question.id]}
            setFreeform={(value) =>
              setFreeformText((prev) => ({...prev, [question.id]: value}))
            }
            selectedAnswers={selectedAnswer}
            onAnswer={onAnswer && handleAnswer}
            onFreeform={onAnswer && handleFreeform}
            correctAnswers={correctAnswers}
          >
            <AttemptFooter
              attempt={attempt}
              nextQuestion={
                currentQuestion < questionCount - 1 ? nextQuestion : undefined
              }
              prevQuestion={currentQuestion > 0 ? prevQuestion : undefined}
              onSubmitAttempt={onSubmitAttempt}
              isSelected={
                selectedAnswer.length > 0 || !!freeformText[question.id]
              }
              isAnsweringQuestion={isAnsweringQuestion}
              isIncompleteAttempt={isIncompleteAttempt}
              onRetry={user.id == attempt.user.id ? onRetry : undefined}
            />
          </SingleQuestionContent>

          <div className="mt-4 mb-1">
            {gt('Answered %s of %s', answerCount, questionCount)}
          </div>
          <SelectionsChecked
            questions={attempt.questions}
            selections={attempt.answers}
            correctAnswers={correctAnswers}
            currentQuestion={currentQuestion}
            gotoQuestion={gotoQuestion}
          />
          <div className="progress my-2">
            <div
              style={{
                width: `${progress}%`,
              }}
              className={cs(`px-1 progress-bar bg-secondary`, {
                'progress-bar-animated progress-bar-striped':
                  attempt.status == 'pending',
              })}
            >
              {progress}%
            </div>
          </div>
        </>
      ) : (
        gt('No question available')
      )}
    </>
  );
};

type FooterProps = {
  attempt: AttemptSerializedType;
  onSubmitAttempt?: () => Promise<void>;
  nextQuestion?: () => unknown;
  prevQuestion?: () => unknown;
  onRetry?: () => Promise<void>;
  isSelected: boolean;
  isAnsweringQuestion?: boolean;
  isIncompleteAttempt: boolean;
};

const AttemptFooter = ({
  attempt,
  nextQuestion,
  prevQuestion,
  onSubmitAttempt,
  isSelected,
  isAnsweringQuestion,
  isIncompleteAttempt,
  onRetry,
}: FooterProps) => {
  return (
    <div className="card-footer mt-4 d-flex justify-content-between">
      <div>
        <button
          disabled={prevQuestion == null}
          className="btn btn-link mr-2"
          onClick={prevQuestion}
        >
          {gt('Prev')}
        </button>
        <button
          disabled={nextQuestion == null}
          className={'btn btn-link'}
          onClick={nextQuestion}
        >
          {gt('Next')}
        </button>
      </div>
      {(nextQuestion == null || !isIncompleteAttempt) && onSubmitAttempt ? (
        <StateButton
          disabled={!isSelected || isAnsweringQuestion}
          className={cs('btn', {
            'btn-link text-muted': isIncompleteAttempt,
            'btn-primary text-white': !isIncompleteAttempt,
          })}
          onClick={onSubmitAttempt}
          busyText={gt('Loading...')}
        >
          {gt('Submit')}
        </StateButton>
      ) : null}
      {onRetry ? (
        <StateButton
          className="btn btn-primary text-white"
          onClick={onRetry}
          busyText={gt('Loading...')}
        >
          {attempt.status === 'pending' ? gt('Continue') : gt('Try Again')}
        </StateButton>
      ) : null}
    </div>
  );
};

const SelectionButton = ({
  n,
  selections,
  question,
  correctAnswers,
  currentQuestion,
  gotoQuestion,
}: SelectionButtonProps) => {
  // Displays a nice square button in the pagination area with correct/incorrect.
  const isCurrentQuestion = currentQuestion + 1 === n;
  const selection = selections.find(
    (selection) => selection.question_id === question.id
  );
  let isCorrect: boolean | undefined = undefined;
  switch (selection?.type) {
    case 'checkbox':
      isCorrect = selection.answer_id.every(
        (answerId) => !!correctAnswers[answerId]
      );
      break;
    case 'freeform':
      isCorrect = true;
      break;
    default:
      isCorrect = undefined;
  }

  return (
    <button onClick={() => gotoQuestion(n - 1)}>
      <div>
        <i
          className={cs('far fa-fw', {
            'fa-check-square': isCorrect,
            'fa-times-square': isCorrect == false,
            'fa-square': isCorrect == undefined,
            'text-success': isCorrect && !isCurrentQuestion,
            'text-danger': isCorrect == false && !isCurrentQuestion,
            'text-muted': isCorrect == undefined && !isCurrentQuestion,
            'text-primary': isCurrentQuestion,
          })}
        ></i>
        <span className="n">{n}</span>
      </div>
    </button>
  );
};

type SelectionsCheckedProps = SelectionProps & {
  questions: Array<QuestionType>;
};

const SelectionsChecked = ({
  questions,
  selections,
  gotoQuestion,
  currentQuestion,
  correctAnswers,
}: SelectionsCheckedProps) => {
  return Object.keys(correctAnswers).length > 0 ? (
    <StyledSelection className="mb-2">
      <PaginationNavigator
        correctAnswers={correctAnswers}
        questions={questions}
        selections={selections}
        currentQuestion={currentQuestion}
        gotoQuestion={gotoQuestion}
      />
    </StyledSelection>
  ) : null;
};

const PaginationNavigator = ({
  questions,
  currentQuestion,
  correctAnswers,
  selections,
  gotoQuestion,
}: SelectionsCheckedProps) => {
  const itemsPerPage = 1;

  const totalPages = Math.ceil(questions.length / itemsPerPage);

  const maxPageNumbers = 40; // Maximum number of page numbers to display
  const pageNumbers: Array<React.ReactNode> = [];

  if (totalPages <= maxPageNumbers) {
    // If total pages is less than or equal to the max page numbers, display all page numbers
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <SelectionButton
          key={questions[i - 1].id}
          correctAnswers={correctAnswers}
          question={questions[i - 1]}
          selections={selections}
          currentQuestion={currentQuestion}
          gotoQuestion={gotoQuestion}
          n={i}
        />
      );
    }
  } else {
    // If total pages is greater than the max page numbers, display a subset of page numbers
    // and add ellipsis if necessary
    let startPage = Math.max(
      currentQuestion + 1 - Math.floor(maxPageNumbers / 2),
      1
    );
    let endPage = startPage + maxPageNumbers - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = endPage - maxPageNumbers + 1;
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <SelectionButton
          key={questions[i - 1].id}
          correctAnswers={correctAnswers}
          question={questions[i - 1]}
          selections={selections}
          gotoQuestion={gotoQuestion}
          currentQuestion={currentQuestion}
          n={i}
        />
      );
    }

    if (startPage > 1) {
      pageNumbers.unshift(
        <span key="ellipsis-1" className="ellipsis">
          ...
        </span>
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-2" className="ellipsis ml-2">
          ...
        </span>
      );
    }
  }

  return <>{pageNumbers}</>;
};

const StyledSelection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  button {
    opacity: 0.7;
    background-color: transparent;
    border: none;
    display: flex;
    gap: 2px;
    padding: 0;
    position: relative;
    i {
      font-size: 2em;
    }
    &:after {
      content: '';
      display: block;
      width: 100%;
      height: 3px;
      margin-top: 15px;
    }
    &:not(:last-child):after {
      background: ${colors.light};
    }
    .n {
      color: ${colors.light};
      font-size: 12px;
    }
  }
`;
